/*
 * The upstream web client uses native `title` attributes for many icon-only
 * controls. Some of those labels are not part of the regular Lingui catalogue,
 * so keep a single Chinese glossary at the DOM boundary. This is intentionally
 * limited to known application controls; note titles and other user content are
 * never changed.
 */
const TOOLTIP_TRANSLATIONS: Record<string, string> = {
  "New tab": "新建标签页",
  Undo: "撤销",
  Redo: "重做",
  Publish: "发布",
  Published: "已发布",
  "Table of contents": "目录",
  Search: "搜索",
  Properties: "属性",
  Save: "保存",
  Close: "关闭",
  "Close others": "关闭其他标签页",
  "Close to right": "关闭右侧标签页",
  "Close to left": "关闭左侧标签页",
  "Close all": "关闭全部标签页",
  "Reveal in list": "在列表中显示",
  Pin: "固定",
  "Open command palette": "打开命令面板",
  "Lock app": "锁定应用",
  "Expand sidebar": "展开侧边栏",
  "Collapse sidebar": "收起侧边栏",
  "Add note": "新建笔记",
  "Add notebook": "新建笔记本",
  "Add tag": "添加标签",
  "Add reminder": "新建提醒",
  Settings: "设置",
  More: "更多操作",
  Edit: "编辑",
  Delete: "删除",
  Remove: "移除",
  Copy: "复制",
  Download: "下载",
  Upload: "上传",
  Refresh: "刷新",
  Retry: "重试",
  Print: "打印",
  Cancel: "取消",
  "Zoom in": "放大",
  "Zoom out": "缩小",
  "Enter fullscreen": "进入全屏",
  "Exit fullscreen": "退出全屏",
  "Focus mode": "专注模式",
  "Normal mode": "普通模式",
  "Enable editor margins": "显示编辑器边距",
  "Disable editor margins": "隐藏编辑器边距",
  "Click to reset zoom": "点击重置缩放",
  Bold: "加粗",
  Italic: "斜体",
  Underline: "下划线",
  Strikethrough: "删除线",
  "Note link": "笔记链接",
  Link: "链接",
  "Edit link": "编辑链接",
  "Remove link": "移除链接",
  "Open link": "打开链接",
  "Copy link": "复制链接",
  "Link settings": "链接设置",
  Code: "行内代码",
  "Remove code": "移除代码格式",
  "Clear formatting": "清除格式",
  Subscript: "下标",
  Superscript: "上标",
  Insert: "插入",
  "Bullet list": "无序列表",
  "Numbered list": "有序列表",
  Checklist: "任务清单",
  "Font family": "字体",
  "Font size": "字号",
  Headings: "标题",
  "Align center": "居中对齐",
  "Align left": "左对齐",
  "Align right": "右对齐",
  "Text direction left to right": "从左到右",
  Highlight: "高亮",
  "Text color": "文字颜色",
  "Insert math": "插入行内公式",
  "Table settings": "表格设置",
  "Column properties": "列属性",
  "Row properties": "行属性",
  "Cell properties": "单元格属性",
  "Insert column left": "在左侧插入列",
  "Insert column right": "在右侧插入列",
  "Move column left": "向左移动列",
  "Move column right": "向右移动列",
  "Delete column": "删除列",
  "Split cells": "拆分单元格",
  "Merge cells": "合并单元格",
  "Insert row above": "在上方插入行",
  "Insert row below": "在下方插入行",
  "Move row up": "向上移动行",
  "Move row down": "向下移动行",
  "Delete row": "删除行",
  "Delete table": "删除表格",
  "Cell background color": "单元格背景颜色",
  "Cell border color": "单元格边框颜色",
  None: "无",
  "Image settings": "图片设置",
  "Image properties": "图片属性",
  "Preview attachment": "预览附件",
  "Attachment settings": "附件设置",
  "Download attachment": "下载附件",
  "Embed settings": "嵌入内容设置",
  "Embed properties": "嵌入内容属性",
  "Web clip settings": "网页剪藏设置",
  Fullscreen: "全屏",
  "Open source": "打开来源",
  Outdent: "减少缩进",
  Indent: "增加缩进",
  "Export CSV": "导出 CSV",
  Paragraph: "段落",
  "Horizontal rule": "分隔线",
  "Code block": "代码块",
  Quote: "引用",
  "Math & formulas": "公式",
  Callout: "提示块",
  Image: "图片",
  "Insert image": "插入图片",
  "Upload from disk": "从设备上传",
  "Take photo using camera": "使用相机拍照",
  Table: "表格",
  "Insert table": "插入表格",
  "Import CSV": "导入 CSV",
  Embed: "嵌入内容",
  "Insert embed": "插入嵌入内容",
  Attachment: "附件",
  "Task list": "任务清单",
  "Outline list": "大纲列表",
  "Attach image from URL": "从链接添加图片",
  "Rotate left": "向左旋转",
  "Rotate right": "向右旋转",
  "Reset image": "重置图片",
  "Open in new tab": "在新标签页打开",
  "Go to previous page": "上一页",
  "Go to next page": "下一页",
  "Clear cache": "清除缓存",
  "Download all attachments": "下载所有附件",
  "Edit notebook": "编辑笔记本",
  "New notebook": "新建笔记本",
  "Remove from recents": "从最近记录中移除",
  "Click to preview": "点击预览",
  "Show more": "显示更多",
  "Hide completed": "隐藏已完成",
  "Show completed": "显示已完成",
  "Go back": "返回",
  "Go forward": "前进",
  "Sync now": "立即同步",
  "Copy as text": "复制为文本",
  "Copy as Markdown": "复制为 Markdown",
  "Export as PDF": "导出为 PDF",
  "Export as Markdown": "导出为 Markdown",
  "Export as HTML": "导出为 HTML"
};

function localizeTitle(element: Element) {
  const title = element.getAttribute("title");
  if (!title) return;

  const translation = TOOLTIP_TRANSLATIONS[title];
  if (translation) element.setAttribute("title", translation);
}

export function installZhTooltipLocalization() {
  if (document.documentElement.lang !== "zh-CN") return;

  document.querySelectorAll("[title]").forEach(localizeTitle);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === "attributes") localizeTitle(mutation.target);
      else {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          localizeTitle(node);
          node.querySelectorAll("[title]").forEach(localizeTitle);
        });
      }
    }
  });

  observer.observe(document.body, {
    attributes: true,
    attributeFilter: ["title"],
    childList: true,
    subtree: true
  });
}
